import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { EnvelopeOpen, CheckCircle, XCircle, Clock, ArrowsClockwise, Calendar } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PILL = {
  pending:   'bg-amber-100 text-amber-800 border-amber-300',
  accepted:  'bg-green-100 text-green-800 border-green-300',
  declined:  'bg-red-100 text-red-700 border-red-300',
  expired:   'bg-gray-200 text-gray-700 border-gray-300',
  superseded:'bg-gray-100 text-text-secondary border-gray-200',
};

const timeLeft = (expiresAtIso) => {
  if (!expiresAtIso) return null;
  const ms = new Date(expiresAtIso).getTime() - Date.now();
  if (ms <= 0) return 'expiring…';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min left`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m left`;
};

/**
 * VendorOffersCard — drop-in card for VendorDashboard. Lists incoming
 * booking offers (decoration/photography/venue/return-gift/flowers/...) with
 * Accept / Decline buttons. After accept, calendar self-blocks for the date;
 * after decline / expire / ignore, a miss is recorded against the vendor.
 */
const VendorOffersCard = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/vendor/offers`, { withCredentials: true });
      setOffers(Array.isArray(data) ? data : []);
    } catch {
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const respond = async (offer, action) => {
    setActing((m) => ({ ...m, [offer.id]: action }));
    try {
      await axios.post(
        `${BACKEND_URL}/api/vendor/offers/${offer.id}/${action}`,
        {},
        { withCredentials: true }
      );
      toast.success(action === 'accept' ? 'Offer accepted — calendar blocked' : 'Offer declined');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setActing((m) => { const cp = { ...m }; delete cp[offer.id]; return cp; });
    }
  };

  const pending = offers.filter((o) => o.status === 'pending');
  const recent = offers.filter((o) => o.status !== 'pending').slice(0, 6);

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="vendor-offers-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <EnvelopeOpen size={20} className="text-brand-primary" /> Booking Offers
              {pending.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 bg-amber-500 text-white rounded font-bold uppercase tracking-wider" data-testid="vendor-offers-pending-count">
                  {pending.length} pending
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Customers send you these directly. Accept fast — 3 misses auto-blocks your calendar for the event date.
            </CardDescription>
          </div>
          <button type="button" onClick={load} disabled={loading} className="text-xs text-text-secondary hover:text-brand-primary inline-flex items-center gap-1" data-testid="vendor-offers-refresh">
            <ArrowsClockwise size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {offers.length === 0 && (
          <p className="text-sm text-text-secondary text-center py-6" data-testid="vendor-offers-empty">
            No booking offers yet.
          </p>
        )}

        {pending.length > 0 && (
          <div className="space-y-2 mb-4">
            {pending.map((o) => (
              <div key={o.id} className="border-2 border-amber-300 rounded p-3 bg-amber-50/40" data-testid={`vendor-offer-${o.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary truncate">
                      {o.cart_item_name}
                    </div>
                    <div className="text-[11px] text-text-secondary flex items-center gap-2 flex-wrap mt-0.5">
                      <span className="uppercase tracking-wider">{o.category}</span>
                      <span className="flex items-center gap-1"><Calendar size={11} /> {o.event_date}</span>
                      <span className="capitalize">{o.event_type}</span>
                      {o.cart_item_price && <span className="font-bold text-brand-primary">₹{Number(o.cart_item_price).toLocaleString('en-IN')}</span>}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-amber-700">
                    <Clock size={11} /> {timeLeft(o.expires_at)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => respond(o, 'accept')}
                    disabled={!!acting[o.id]}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-none flex-1"
                    data-testid={`accept-offer-${o.id}`}
                  >
                    <CheckCircle size={14} className="mr-1" weight="fill" /> Accept
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => respond(o, 'decline')}
                    disabled={!!acting[o.id]}
                    className="rounded-none border-red-300 text-red-600 hover:bg-red-50 flex-1"
                    data-testid={`decline-offer-${o.id}`}
                  >
                    <XCircle size={14} className="mr-1" /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {recent.length > 0 && (
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-text-secondary mb-1">Recent</div>
            {recent.map((o) => (
              <div key={o.id} className="flex items-center justify-between text-xs py-1.5 px-2 border-b border-[#E8E1D5]" data-testid={`vendor-offer-recent-${o.id}`}>
                <span className="flex-1 truncate text-text-secondary">{o.cart_item_name}</span>
                <span className="text-[10px] text-text-secondary mr-2">{o.event_date}</span>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${PILL[o.status] || PILL.pending}`}>
                  {o.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VendorOffersCard;
